/**
 * AriaMirror — gir skjermlesere tilgang til viktige spill-states uten å
 * måtte bygge en full DOM-overlay-arkitektur.
 *
 * Bakgrunn (runde 41):
 * Phaser-canvas er én DOM-node og kan ikke selv eksponere indre tilstand
 * til assistive teknologi. Vi løser det med en skjult ARIA-live-region
 * som vi oppdaterer fra viktige steder (HUD-state, modal-åpning, toasts).
 *
 * Strategi:
 * - `polite`-region for HUD-oppdateringer (kroner, dag, sesong, tier)
 *   som ikke trenger å avbryte annen tale, men skal leses opp.
 * - `assertive`-region for toasts og kritiske varsler.
 * - Visuelt skjult med standard `clip` + offscreen-teknikk.
 *
 * Bruk:
 *   ariaMirror.start();
 *   ariaMirror.varselHud({ kroner: 200000, dag: 12, sesong: 'vinter', tier: 'D' });
 *   ariaMirror.varselModal('Forfattere');
 *   ariaMirror.varselToast('Ny anmeldelse: 4 stjerner');
 */

const HUD_ID = 'forlagshuset-aria-hud';
const TOAST_ID = 'forlagshuset-aria-toast';

const SKJULT_STIL = [
  'position:absolute',
  'left:-9999px',
  'width:1px',
  'height:1px',
  'overflow:hidden',
  'clip:rect(0 0 0 0)',
  'clip-path:inset(50%)',
  'white-space:nowrap',
].join(';');

export interface HudVarsel {
  kroner: number;
  dag: number;
  sesong: string;
  tier: string;
}

export class AriaMirror {
  private hudEl?: HTMLDivElement;
  private toastEl?: HTMLDivElement;
  private sisteHudTekst = '';

  /**
   * Initialiserer ARIA-elementene i DOM. Trygt å kalle flere ganger;
   * eksisterende elementer gjenbrukes hvis de finnes.
   */
  start(): void {
    if (typeof document === 'undefined') return;

    let hud = document.getElementById(HUD_ID) as HTMLDivElement | null;
    if (!hud) {
      hud = document.createElement('div');
      hud.id = HUD_ID;
      hud.setAttribute('aria-live', 'polite');
      hud.setAttribute('aria-atomic', 'true');
      hud.setAttribute('role', 'status');
      hud.setAttribute('style', SKJULT_STIL);
      document.body.appendChild(hud);
    }
    this.hudEl = hud;

    let toast = document.getElementById(TOAST_ID) as HTMLDivElement | null;
    if (!toast) {
      toast = document.createElement('div');
      toast.id = TOAST_ID;
      toast.setAttribute('aria-live', 'assertive');
      toast.setAttribute('aria-atomic', 'true');
      toast.setAttribute('role', 'alert');
      toast.setAttribute('style', SKJULT_STIL);
      document.body.appendChild(toast);
    }
    this.toastEl = toast;
  }

  /**
   * Oppdaterer HUD-tekst. Hvis tekst er identisk med forrige, gjøres
   * ingenting — vi vil unngå at skjermlesere repeterer samme info.
   */
  varselHud(v: HudVarsel): void {
    if (!this.hudEl) return;
    const tekst = formaterHud(v);
    if (tekst === this.sisteHudTekst) return;
    this.sisteHudTekst = tekst;
    this.hudEl.textContent = tekst;
  }

  /**
   * Annoncer at en modal er åpnet. Vises kort i HUD-regionen så
   * skjermleseren får konteksten.
   */
  varselModal(tittel: string): void {
    if (!this.hudEl) return;
    const tekst = `Åpnet: ${tittel}`;
    this.sisteHudTekst = tekst;
    this.hudEl.textContent = tekst;
  }

  /**
   * Annoncer en FAB-utvidelse: leser opp listen av tilgjengelige
   * aksjoner. Brukes når aksjons-fanen ekspanderer.
   */
  varselFabMeny(aksjoner: string[]): void {
    if (!this.hudEl || aksjoner.length === 0) return;
    const tekst = `Aksjoner: ${aksjoner.join(', ')}`;
    this.sisteHudTekst = tekst;
    this.hudEl.textContent = tekst;
  }

  /**
   * Vis en toast-melding i assertive-regionen så skjermleseren leser
   * den umiddelbart. Tidsbegrenset levetid for å unngå støy.
   */
  varselToast(tekst: string): void {
    if (!this.toastEl) return;
    this.toastEl.textContent = tekst;
    // Rens etter 4 sekunder for å unngå at gammel toast leses opp igjen
    // ved navigering med leseren.
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        if (this.toastEl && this.toastEl.textContent === tekst) {
          this.toastEl.textContent = '';
        }
      }, 4000);
    }
  }

  /**
   * Rydder DOM-elementene. Brukes i tester for å unngå at de henger
   * igjen mellom testkjøringer.
   */
  stopp(): void {
    if (this.hudEl?.parentNode) this.hudEl.parentNode.removeChild(this.hudEl);
    if (this.toastEl?.parentNode) this.toastEl.parentNode.removeChild(this.toastEl);
    this.hudEl = undefined;
    this.toastEl = undefined;
    this.sisteHudTekst = '';
  }
}

function formaterHud(v: HudVarsel): string {
  const krFormatert = v.kroner.toLocaleString('nb-NO');
  return `Kroner: ${krFormatert}. Dag: ${v.dag}. Sesong: ${v.sesong}. Tier: ${v.tier}.`;
}

export const ariaMirror = new AriaMirror();
